import { extractText } from "./message-extract.ts";
import { isAssistantMessage } from "./message-transcript.ts";

/**
 * The plan-approval ack.
 *
 * The gateway writes it as a plain-text assistant message
 * (`formatFleetCoordinatorAck` in src/gateway/session-dialogue/
 * session-fleet-intent.ts) — three lines of prose:
 *
 *   Plan 5ab57b89 — fleet unit dispatched (serial plan).
 *   Plan approved — spawning first batch (1 runnable now, 6 total). Use plan_work(action=status) for progress.
 *   Track progress in the orchestration group; unit results appear as [subagent-result] rows.
 *
 * Nothing in the chat treated it as a status event, so it fell through to
 * the generic assistant bubble and read as one more wall of monospace
 * prose — the moment of approval was the least legible thing in the
 * thread, while the plan *status* got a full dashboard (JAISIU-4014).
 *
 * This module reads the ack back into facts so the thread can render it
 * as a card. It parses the text rather than adding a new wire format
 * because the ack is already in every existing transcript: a format
 * change would only upgrade new approvals, leaving the ones the operator
 * is looking at right now exactly as ugly.
 */

/** Em dash / en dash / hyphen — the producer writes an em dash. */
const DASH = "[—–-]";

/**
 * The dispatch line is the one line of the ack that ordinary assistant
 * prose never produces, so it carries the detection. Both variants are
 * matched: a serial plan and a parallel plan phrase it differently.
 */
const DISPATCH_SERIAL_RE = new RegExp(
  `^(?:Plan\\s+([A-Za-z0-9_-]{1,40})|Fleet plan)\\s+${DASH}\\s*fleet unit dispatched\\b`,
  "im",
);
const DISPATCH_PARALLEL_RE = new RegExp(
  `^(?:Plan\\s+([A-Za-z0-9_-]{1,40})|Fleet plan)\\s+${DASH}\\s*(\\d+)\\s+unit\\(s\\)\\s+dispatched in parallel\\b`,
  "im",
);

/** `Plan approved — spawning first batch (1 runnable now, 6 total).` */
const APPROVED_SPAWN_RE = new RegExp(
  `^Plan approved\\s+${DASH}\\s+spawning first batch\\s*\\(\\s*(\\d+)\\s+runnable now\\s*,\\s*(\\d+)\\s+total\\s*\\)`,
  "im",
);

/** `Work plan started — 3 spawned now (2 runnable), 6 total unit(s).` */
const STARTED_RE = new RegExp(
  `^Work plan started\\s+${DASH}\\s+(\\d+)\\s+spawned now\\s*\\(\\s*(\\d+)\\s+runnable\\s*\\)\\s*,\\s*(\\d+)\\s+total unit\\(s\\)`,
  "im",
);

/** `Work plan proposed (6 units). Call plan_work(action=start, …).` */
const PROPOSED_RE = /^Work plan proposed\s*\(\s*(\d+)\s+units?\s*\)/im;

/** `Plan approved — no runnable units to spawn yet` */
const NO_RUNNABLE_RE = new RegExp(`^Plan approved\\s+${DASH}\\s+no runnable units`, "im");

/** The closing line telling the operator where to watch the run. */
const HINT_RE = /^Track progress in the orchestration group.*$/im;

export type PlanApprovalFacts = {
  /** Short status word lifted into the card header. */
  headline: string;
  /** Short plan id the operator can quote back, when the ack carried one. */
  planId: string | null;
  /** Units whose dependencies are satisfied and which can start now. */
  runnableNow: number | null;
  /** Total units in the plan. */
  unitCount: number | null;
  /** Units already in flight when the ack was written. */
  dispatched: number | null;
  /** True when the plan runs one unit at a time instead of in parallel. */
  serial: boolean;
  /** The "where to watch it" line, verbatim. */
  hint: string | null;
};

/**
 * Read a plan-approval ack back into facts, or `null` when the text is
 * not one.
 *
 * Every field is optional: the producer has several ack shapes (approved,
 * started, proposed, "no runnable units") and a new one may add fields
 * this parser does not know yet. A missing field drops its chip rather
 * than failing the card.
 */
export function parsePlanApproval(text: string): PlanApprovalFacts | null {
  const src = (text ?? "").trim();
  if (!src) {
    return null;
  }

  const serial = DISPATCH_SERIAL_RE.exec(src);
  const parallel = serial ? null : DISPATCH_PARALLEL_RE.exec(src);
  const dispatch = serial ?? parallel;
  if (!dispatch) {
    return null;
  }

  const planId = dispatch[1]?.trim() ? dispatch[1].trim() : null;

  let headline = "Plan approved";
  let runnableNow: number | null = null;
  let unitCount: number | null = null;

  const approved = APPROVED_SPAWN_RE.exec(src);
  const started = approved ? null : STARTED_RE.exec(src);
  const proposed = approved || started ? null : PROPOSED_RE.exec(src);

  if (approved) {
    runnableNow = Number(approved[1]);
    unitCount = Number(approved[2]);
  } else if (started) {
    headline = "Work plan started";
    runnableNow = Number(started[2]);
    unitCount = Number(started[3]);
  } else if (proposed) {
    headline = "Work plan proposed";
    unitCount = Number(proposed[1]);
  } else if (NO_RUNNABLE_RE.test(src)) {
    runnableNow = 0;
  }

  const dispatched = parallel ? Number(parallel[2]) : serial ? 1 : null;

  const hintMatch = HINT_RE.exec(src);
  const hint = hintMatch ? hintMatch[0].trim() : null;

  return {
    headline,
    planId,
    runnableNow,
    unitCount,
    dispatched,
    serial: Boolean(serial),
    hint,
  };
}

/**
 * True when the message is the gateway's plan-approval ack.
 *
 * Only assistant messages qualify: the ack is written as one, and
 * restricting the role keeps a user quoting the same words out of the
 * card path.
 */
export function isPlanApprovalMessage(message: unknown): boolean {
  if (!isAssistantMessage(message)) {
    return false;
  }
  return parsePlanApproval(extractText(message) ?? "") !== null;
}
